// "Not this version" for the core-update offer (ADR 0178's banner).
//
// The offer sits above every boat route and on every fleet card, so an owner who
// is not ready to update saw it on every screen with no way to put it down. The
// close button writes the version that was dismissed — NOT a plain flag — so the
// next published release brings the offer back on its own. Dismissing means "not
// this version", never "stop telling me".
//
// `localStorage` rather than a server column, for the reason `vesselType.ts`
// gives about its own "Not now": this is a preference about a nag, not about the
// boat, and it should not cost a migration or follow the owner onto a screen
// where a different person is deciding.

/** Where a dismissal is remembered. Per BOAT — a two-boat owner updates them on different weekends. */
export function updateBannerDismissKey(boatId: string): string {
  return `nm:update-banner-dismissed:${boatId}`;
}

/** The version dismissed on THIS browser for this boat, or null if none. */
export function dismissedUpdateVersion(boatId: string): string | null {
  try {
    return window.localStorage.getItem(updateBannerDismissKey(boatId));
  } catch {
    // Private mode / storage disabled. Showing the offer again is the safe
    // failure: the alternative is hiding an update the owner never declined.
    return null;
  }
}

/** Remember that this version's offer was closed. Storage failures are silent — see above. */
export function dismissUpdateVersion(boatId: string, version: string): void {
  try {
    window.localStorage.setItem(updateBannerDismissKey(boatId), version);
  } catch {
    /* nothing to do — the banner simply returns next visit */
  }
}

// The firmware offer's dismissal (ADR 0202) — its own key, same semantics:
// "not this version", never "stop telling me". Kept separate from the core
// key because the two offers are separate decisions an owner can make on
// different weekends.

export function firmwareBannerDismissKey(boatId: string): string {
  return `nm:firmware-banner-dismissed:${boatId}`;
}

/** The firmware version dismissed on THIS browser for this boat, or null. */
export function dismissedFirmwareVersion(boatId: string): string | null {
  try {
    return window.localStorage.getItem(firmwareBannerDismissKey(boatId));
  } catch {
    return null;
  }
}

/** Remember that this firmware version's offer was closed. */
export function dismissFirmwareVersion(boatId: string, version: string): void {
  try {
    window.localStorage.setItem(firmwareBannerDismissKey(boatId), version);
  } catch {
    /* nothing to do — the banner simply returns next visit */
  }
}
